<template>
  <div class="bw-card">
    <div class="bw-chart">
      <canvas ref="canvasEl"></canvas>
    </div>

    <div class="bw-summary">
      <div class="bw-pill">
        <div class="bw-pill-title">Bed Time Variability</div>
        <div class="bw-pill-value">{{ summary.bedVariabilityLabel }}</div>
      </div>

      <div class="bw-pill">
        <div class="bw-pill-title">Wake Time Variability</div>
        <div class="bw-pill-value">{{ summary.wakeVariabilityLabel }}</div>
      </div>

      <div class="bw-pill">
        <div class="bw-pill-title">Bed Time Range</div>
        <div class="bw-pill-value">{{ summary.bedRangeLabel }}</div>
      </div>

      <div class="bw-pill">
        <div class="bw-pill-title">Wake Time Range</div>
        <div class="bw-pill-value">{{ summary.wakeRangeLabel }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
);

// Time-axis strategy: 22:00 → 09:00 next day
const BASE_MINUTES = 22 * 60; // 22:00
const VIEW_MIN = 0;           // 22:00
const VIEW_MAX = 11 * 60;     // 09:00 next day

function hhmmToMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function toOffsetMinutes(hhmm) {
  const t = hhmmToMinutes(hhmm);
  return t >= BASE_MINUTES ? (t - BASE_MINUTES) : (t + 24 * 60 - BASE_MINUTES);
}

function offsetToLabel(offsetMin) {
  const total = BASE_MINUTES + offsetMin;
  const minsOfDay = ((total % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hh = String(Math.floor(minsOfDay / 60)).padStart(2, "0");
  const mm = String(minsOfDay % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

function median(values) {
  if (!values.length) return 0;
  const a = [...values].sort((x, y) => x - y);
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}

function variabilityMAD(values) {
  if (!values.length) return 0;
  const med = median(values);
  const absDevs = values.map((v) => Math.abs(v - med));
  return Math.round(median(absDevs));
}

function formatMinutes(mins) {
  return `${mins}m`;
}

export default {
  name: "UserBedWakeChart",
  props: {
    /**
     * items: [
     *  { dateLabel: "Nov 14\nTUE", bed: "23:15", wake: "07:15" },
     * ]
     */
    //items: { type: Array, required: true },
    items: [
       { dateLabel: "Nov 14\nTUE", bed: "23:15", wake: "07:15" },
  { dateLabel: "Nov 15\nWED", bed: "22:45", wake: "06:45" },
  { dateLabel: "Nov 16\nTHU", bed: "23:00", wake: "08:45" },
  { dateLabel: "Nov 17\nFRI", bed: "23:30", wake: "07:10" },
  { dateLabel: "Nov 18\nSAT", bed: "23:38", wake: "07:16" },
  { dateLabel: "Nov 19\nSUN", bed: "23:43", wake: "07:00" },
  { dateLabel: "Nov 20\nMON", bed: "23:30", wake: "06:28" },
  { dateLabel: "Nov 21\nTUE", bed: "23:30", wake: "06:15" },
  { dateLabel: "Nov 22\nWED", bed: "23:30", wake: "06:22" },
  { dateLabel: "Nov 23\nTHU", bed: "23:30", wake: "04:50" },
  { dateLabel: "Nov 24\nFRI", bed: "23:10", wake: "04:22" },
  { dateLabel: "Nov 25\nSAT", bed: "23:10", wake: "04:15" },
  { dateLabel: "Nov 26\nSUN", bed: "23:50", wake: "05:10" },
  { dateLabel: "Nov 27\nMON", bed: "23:00", wake: "04:00" },
    ]
  },
  data() {
    return {
      chart: null,
      bedLabelPluginId: "bedLabels",
    };
  },
  computed: {
    normalized() {
      return (this.items || []).map((d) => {
        const bedOff = toOffsetMinutes(d.bed);
        const wakeOff = toOffsetMinutes(d.wake);
        const start = Math.min(bedOff, wakeOff);
        const end = Math.max(bedOff, wakeOff);

        return {
          ...d,
          bedOff,
          wakeOff,
          range: [start, end],
        };
      });
    },
    summary() {
      if (!this.normalized.length) {
        return {
          bedVariabilityLabel: "-",
          wakeVariabilityLabel: "-",
          bedRangeLabel: "-",
          wakeRangeLabel: "-",
        };
      }

      const beds = this.normalized.map((x) => x.bedOff);
      const wakes = this.normalized.map((x) => x.wakeOff);

      const bedVar = variabilityMAD(beds);
      const wakeVar = variabilityMAD(wakes);

      const bedMin = Math.min(...beds);
      const bedMax = Math.max(...beds);
      const wakeMin = Math.min(...wakes);
      const wakeMax = Math.max(...wakes);

      return {
        bedVariabilityLabel: formatMinutes(bedVar),
        wakeVariabilityLabel: formatMinutes(wakeVar),
        bedRangeLabel: `${offsetToLabel(bedMin)} - ${offsetToLabel(bedMax)}`,
        wakeRangeLabel: `${offsetToLabel(wakeMin)} - ${offsetToLabel(wakeMax)}`,
      };
    },
  },
  mounted() {
    this.renderChart();
  },
  beforeUnmount() {
    this.destroyChart();
  },
  watch: {
    items: {
      deep: true,
      handler() {
        this.renderChart();
      },
    },
  },
  methods: {
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    getBedLabelsPlugin() {
      // Custom plugin to render bed-time labels at the bottom of each bar
      return {
        id: this.bedLabelPluginId,
        afterDatasetsDraw: (chart) => {
          const { ctx } = chart;
          const meta = chart.getDatasetMeta(0);
          const ds = chart.data.datasets[0];

          ctx.save();
          ctx.font =
            "600 11px system-ui, -apple-system, Segoe UI, Roboto, Arial";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";

          meta.data.forEach((bar, i) => {
            const point = ds.data[i];
            const range = point && point.y;
            if (!Array.isArray(range)) return;

            const bedLabel = offsetToLabel(range[0]);
            const x = bar.x;
            const yBottom = Math.max(bar.y, bar.base);

            ctx.fillText(bedLabel, x, yBottom + 4);
          });

          ctx.restore();
        },
      };
    },
    renderChart() {
      const canvas = this.$refs.canvasEl;
      if (!canvas) return;

      // Recreate to keep it simple + reliable
      this.destroyChart();

      const labels = this.normalized.map((d) => d.dateLabel);

      const points = this.normalized.map((d) => ({
        x: d.dateLabel,
        y: d.range,
      }));

      this.chart = new Chart(canvas.getContext("2d"), {
        type: "bar",
        data: {
          labels, // keep for axis ticks
          datasets: [
            {
              label: "Bed/ Wake Time",
              data: points,
              borderRadius: 10,
              borderSkipped: false,
              barThickness: 10,
              backgroundColor: "#7C3AED", // set explicitly so it doesn't blend with dark bg
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          // Important: ensure the dataset uses x/y as-is
          parsing: false,

          plugins: {
            legend: { display: true },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const y = ctx.raw?.y;
                  const start = Array.isArray(y) ? y[0] : null;
                  const end = Array.isArray(y) ? y[1] : null;
                  if (start == null || end == null) return "";
                  return `Bed ${offsetToLabel(start)}  •  Wake ${offsetToLabel(end)}`;
                },
              },
            },
            datalabels: {
              formatter: (raw) => {
                const y = raw?.y;
                if (!Array.isArray(y)) return "";
                return offsetToLabel(y[1]);
              },
              anchor: "end",
              align: "top",
              offset: 2,
              clamp: true,
              font: { size: 11, weight: "600" },
              color: "rgba(255,255,255,0.85)",
            },
          },
          scales: {
            x: { grid: { display: false } },
            y: {
              min: VIEW_MIN,
              max: VIEW_MAX,
              ticks: {
                stepSize: 60,
                callback: (v) => offsetToLabel(v),
              },
              title: { display: true, text: "BED / WAKE TIME" },
            },
          },
        },
        plugins: [this.getBedLabelsPlugin()],
      });
    },
  },
};
</script>

<style scoped>
.bw-card {
  background: #1f2a2e;
  padding: 16px;
  border-radius: 12px;
}

.bw-chart {
  height: 380px;
}

.bw-summary {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.bw-pill {
  background: #0f171a;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 10px 12px;
}

.bw-pill-title {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 4px;
}

.bw-pill-value {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 700;
}
</style>
